import { ClientSelector } from 'components/secondaryMarket/shared/components/clientSelectors';
import { FeedbackMessage } from 'components/secondaryMarket/shared/FeedbackMessage';
import { Label, LongLabel } from 'components/secondaryMarket/shared/Label';
import type { ProductSummaryProps } from 'components/secondaryMarket/shared/sections/SecondaryProductSummary.connect';
import { ButtonsGroup } from 'components/shared/ButtonsGroup/ButtonsGroup';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { validateClient, validateOtcClient } from 'state/secondaryMarket/cpOrder/cpOrder.utils';
import { displayDateFormatOptions } from 'utils/date';

import { Block, VerticalFormGroup } from '..';
import type { ClientType } from '../../../../state/secondaryMarket/shared/form.models';
import { OtcClientSelector, SaleSelector, SalesDeskSelector } from '../components';

const fieldIds = [
  'clientType',
  'isin',
  'fileCode',
  'quotationType',
  'priceType',
  'maturityDate',
  'denomination',
  'client',
  'sales',
  'salesDesk',
  'traderMessage',
  'saleMessage',
  'ric',
  'salesId',
  'tradingMessage',
  'traderGroup',
  'portfolio',
] as const;

type FieldId = (typeof fieldIds)[number];
export const SecondaryProductSummary: FC<ProductSummaryProps> = ({
  isOtcProduct,
  isinCode,
  fileCode,
  maturityDate,
  quotationType,
  priceType,
  clientType,
  isClientVisible,
  isSalesVisible,
  traderGroup,
  traderMessage,
  saleMessage,
  portfolio,
  isFrozen,
  ric,
  clientLoadingStatus,
  productClients,
  productOtcClients,
  clientEliotCode,
  client,
  availableSales,
  availableDesks,
  onSalesDeskChanged,
  selectedSalesDesk,
  salesId,
  onClientTypeChanged,
  onClientClear,
  onClientChanged,
  onSalesChanged,
}) => {
  const { formatMessage, formatDate } = useIntl();
  const availableClientTypes: ReadonlyArray<ClientType> = ['Pre-hedge', 'Pending', 'Other'];
  const getErrorForClient = useCallback(
    (selected: ISalesCounterpart) => {
      if (productClients?.some(c => c.id === selected.id)) {
        return []; // order book client can always be selected,
      }
      return validateClient(selected);
    },
    [productClients],
  );

  return (
    <Block label="form.ProductSummary" id="product-summary" fieldIds={fieldIds}>
      <form name="marketProductSummary" noValidate autoComplete="off">
        <div className="row">
          <VerticalFormGroup<FieldId>
            inputId="isin"
            label="secondaryMarket.productSummary.label.isin"
            className="col-6"
          >
            {isinCode !== undefined && <Label<FieldId> id="isin" value={isinCode} e2e="isin-code" />}
          </VerticalFormGroup>
          <VerticalFormGroup<FieldId>
            inputId="fileCode"
            label="secondaryMarket.productSummary.label.fileCode"
            className="col-6"
          >
            {fileCode !== undefined && <Label<FieldId> id="fileCode" value={fileCode} e2e="file-code" />}
          </VerticalFormGroup>
        </div>
        <div className="row">
          <VerticalFormGroup<FieldId> inputId="ric" label="secondaryMarket.productSummary.label.ric" className="col-6">
            {ric !== undefined && <Label<FieldId> id="ric" value={ric} e2e="ric" />}
          </VerticalFormGroup>
          <VerticalFormGroup<FieldId>
            inputId="quotationType"
            label="secondaryMarket.productSummary.label.quotationType"
            className="col-6"
          >
            {quotationType !== undefined && (
              <Label<FieldId>
                id="quotationType"
                value={formatMessage({ id: `secondaryMarket.productSummary.quotationType.${quotationType}` })}
                e2e="quotation-type"
              />
            )}
          </VerticalFormGroup>
        </div>
        <div className="row">
          <VerticalFormGroup<FieldId>
            inputId="maturityDate"
            label="secondaryMarket.productSummary.label.maturityDate"
            className="col-6"
          >
            {maturityDate && (
              <Label<FieldId>
                id="maturityDate"
                value={formatDate(maturityDate, displayDateFormatOptions)}
                e2e="maturity-date"
              />
            )}
          </VerticalFormGroup>
          <VerticalFormGroup<FieldId>
            inputId="priceType"
            label="secondaryMarket.productSummary.label.priceType"
            className="col-6"
          >
            {priceType !== undefined && <Label<FieldId> id="priceType" value={priceType} e2e="price-type" />}
          </VerticalFormGroup>
        </div>

        {!isOtcProduct && (
          <div className="row">
            <VerticalFormGroup<FieldId>
              inputId="clientType"
              label="secondaryMarket.productSummary.label.clientType"
              className="col-12"
            >
              <ButtonsGroup
                className="w-100"
                items={availableClientTypes}
                selectedItems={clientType}
                onSelect={items => onClientTypeChanged(items[0])}
                isRadio
                translationPrefix="form.clientType"
                isDisabled={isFrozen}
                e2e="client-type-buttons"
              />
            </VerticalFormGroup>
          </div>
        )}
        {isClientVisible && (
          <div className="row" data-e2e="client-search">
            <VerticalFormGroup<FieldId>
              inputId="client"
              label="secondaryMarket.productSummary.label.client"
              className="col-12"
            >
              {isOtcProduct ? (
                <OtcClientSelector
                  className="flex-grow-1 justify-content-start min-width-0"
                  otcClientLoadingStatus={clientLoadingStatus}
                  productOtcClients={productOtcClients}
                  selectedClientEliotCode={clientEliotCode}
                  isDisabled={isFrozen}
                  onClientChanged={onClientChanged}
                  onClientClear={onClientClear}
                  getErrors={validateOtcClient}
                />
              ) : (
                <ClientSelector
                  className="flex-grow-1 justify-content-start min-width-0"
                  isDisabled={isFrozen}
                  productClients={productClients}
                  selectedClient={client}
                  loadingStatus={clientLoadingStatus}
                  searchActiveOnly={false}
                  onClientClear={onClientClear}
                  onClientChanged={onClientChanged}
                  getErrors={getErrorForClient}
                />
              )}
              {clientLoadingStatus === 'error' && !isSalesVisible && (
                <FeedbackMessage labelKey="secondaryMarket.errors.defaultClientLoadFailed" color="danger" />
              )}
            </VerticalFormGroup>
          </div>
        )}
        {isClientVisible && isSalesVisible && (
          <div className="row" data-e2e="sales-block">
            <VerticalFormGroup<FieldId>
              inputId="salesDesk"
              label="secondaryMarket.productSummary.label.salesDesk"
              className="col-6"
            >
              <SalesDeskSelector
                displayErrors={false}
                availableDesks={availableDesks}
                salesDeskLoadingStatus={clientLoadingStatus}
                isFrozen={isFrozen}
                onSalesDeskChanged={onSalesDeskChanged}
                selectedSalesDesk={selectedSalesDesk}
              />
            </VerticalFormGroup>
            <VerticalFormGroup<FieldId>
              inputId="salesId"
              label="secondaryMarket.productSummary.label.sales"
              className="col-6"
            >
              <SaleSelector
                displayErrors={false}
                allSales={availableSales}
                isFrozen={isFrozen}
                salesLoadingStatus={clientLoadingStatus}
                salesId={salesId}
                onSalesChanged={onSalesChanged}
              />
            </VerticalFormGroup>
          </div>
        )}

        <div className="row">
          <VerticalFormGroup<FieldId>
            inputId="tradingMessage"
            label="secondaryMarket.productSummary.label.tradingMessage"
          >
            <LongLabel id="tradingMessage" value={traderMessage} e2e="trading-message" />
          </VerticalFormGroup>
        </div>
        <div className="row">
          <VerticalFormGroup<FieldId> inputId="saleMessage" label="secondaryMarket.productSummary.label.saleMessage">
            <LongLabel id="saleMessage" value={saleMessage} e2e="sale-message" />
          </VerticalFormGroup>
        </div>
        <div className="row">
          <VerticalFormGroup<FieldId>
            inputId="traderGroup"
            label="secondaryMarket.productSummary.label.traderGroup"
            className="col-6"
          >
            {traderGroup && <Label<FieldId> id="traderGroup" value={traderGroup} e2e="trader-group" />}
          </VerticalFormGroup>
          <VerticalFormGroup<FieldId>
            inputId="portfolio"
            label="secondaryMarket.productSummary.label.portfolio"
            className="col-6"
          >
            {portfolio && <Label<FieldId> id="portfolio" value={portfolio} e2e="portfolio" />}
          </VerticalFormGroup>
        </div>
      </form>
    </Block>
  );
};

SecondaryProductSummary.displayName = 'LiveProductSummary';
